/**
 * Lexicon used by the deterministic message parser.
 *
 * Everything here is data, not logic, so adding a bank or a language is a
 * matter of extending arrays rather than editing the parser.
 */
export const CURRENCY_TOKENS = [
    { token: 'AED', code: 'AED' }, { token: 'DHS', code: 'AED' }, { token: 'DH', code: 'AED' },
    { token: 'د\\.?إ', code: 'AED' }, { token: 'درهم', code: 'AED' }, { token: 'إ\\.د', code: 'AED' },
    { token: 'SAR', code: 'SAR' }, { token: 'SR', code: 'SAR' }, { token: 'ر\\.?س', code: 'SAR' }, { token: 'ريال', code: 'SAR' },
    { token: 'QAR', code: 'QAR' }, { token: 'ر\\.?ق', code: 'QAR' },
    { token: 'KWD', code: 'KWD' }, { token: 'د\\.?ك', code: 'KWD' },
    { token: 'BHD', code: 'BHD' }, { token: 'OMR', code: 'OMR' },
    { token: 'USD', code: 'USD' }, { token: '\\$', code: 'USD' },
    { token: 'EUR', code: 'EUR' }, { token: '€', code: 'EUR' },
    { token: 'GBP', code: 'GBP' }, { token: '£', code: 'GBP' },
    { token: 'INR', code: 'INR' }, { token: '₹', code: 'INR' }, { token: 'RS', code: 'INR' },
    { token: 'EGP', code: 'EGP' }, { token: 'JPY', code: 'JPY' },
];
/** Alternation of every currency token, longest first so "AED" beats "D". */
export const CURRENCY_ALT = CURRENCY_TOKENS
    .map((c) => c.token)
    .sort((a, b) => b.length - a.length)
    .join('|');
export function currencyCodeForToken(token) {
    const clean = token.replace(/[.\s]/g, '').toUpperCase();
    for (const c of CURRENCY_TOKENS) {
        const t = c.token.replace(/\\/g, '').replace(/[.\s]/g, '').toUpperCase();
        if (t === clean)
            return c.code;
    }
    return 'AED';
}
/** Words indicating money left the account. */
export const DEBIT_KEYWORDS = [
    'spent', 'purchase', 'purchased', 'debited', 'debit', 'withdrawn', 'withdrawal',
    'deducted', 'charged', 'charge', 'paid', 'payment of', 'was used', 'has been used',
    'used for', 'pos transaction', 'card transaction', 'bill payment', 'transferred to',
    'sent to', 'billed',
    // Arabic
    'تم خصم', 'خصم', 'مشتريات', 'شراء', 'سحب', 'دفع', 'تم استخدام', 'تم الدفع', 'صرف',
];
/** Words indicating money entered the account. */
export const CREDIT_KEYWORDS = [
    'credited', 'credit of', 'received', 'deposited', 'deposit of', 'salary',
    'has been credited', 'refunded', 'refund of', 'reversal', 'reversed', 'cashback',
    'transferred from', 'incoming transfer', 'added to your account',
    // Arabic
    'تم إيداع', 'إيداع', 'ايداع', 'تم إضافة', 'إضافة', 'راتب', 'استرداد', 'تم استرجاع', 'حوالة واردة',
];
/**
 * Which side of a transfer the user's own account is on.
 *
 * Decisive, and checked before any plain debit or credit word, because those
 * get this wrong: "transferred from" is in CREDIT_KEYWORDS — right for "AED 500
 * transferred from AHMED K", and exactly backwards for "AED 5,000 transferred
 * from your account to your SAVINGS account", which read as income.
 *
 * Naming the account side avoids the trap: whatever else the sentence says,
 * money moving *from your account* left.
 */
export const TRANSFER_FROM_OWN_ACCOUNT = [
    'from your account', 'from your acc', 'from your a/c', 'from a/c',
    'from your card', 'from account',
    'من حسابك', 'من حساب', 'من بطاقتك',
];
export const TRANSFER_TO_OWN_ACCOUNT = [
    'to your account', 'to your acc', 'to your a/c', 'added to your account',
    'incoming transfer', 'transfer received',
    'إلى حسابك', 'الى حسابك', 'حوالة واردة', 'إلى حسابكم',
];
/**
 * A transfer that lands somewhere the user already owns.
 *
 * Patterns rather than a keyword list, because banks name the account in the
 * middle of the phrase — "to your SAVINGS account", "to your Emirates NBD
 * current account" — and a fixed string only ever catches the plainest of them.
 * Getting this wrong in either direction is costly: miss one and a savings
 * top-up is offered up as spending, over-match and a payment to a landlord is
 * never asked about at all.
 */
export const TRANSFER_TO_OWN_PATTERNS = [
    /\bto\s+your\s+(?:[A-Za-z]+\s+){0,3}(?:account|a\/c|card|wallet|deposit)/i,
    /\b(?:moved|transferred|credited)\s+to\s+your\b/i,
    /(?:إلى|الى)\s*حساب(?:ك|كم)/,
];
/**
 * Paying your own credit card, from either side.
 *
 * The bank reports it twice — once from the account it left, once as received
 * on the card — and both read like a payment to somebody. Neither is spending:
 * the purchases on that card were already counted when they were made, and
 * counting the bill as well would double the whole month.
 */
export const CARD_BILL_PATTERNS = [
    /credit\s*card\s*payment/i,
    /payment\s+(?:to|towards)\s+your\s+card/i,
    /(?:إلى|الى)\s*بطاقت/,
    /دفع\s*إلى\s*بطاقة\s*الائتمان/,
    /استلام\s*دفعة/,
];
/** Plain movement words, used only when neither account side is named. */
export const TRANSFER_OUT_KEYWORDS = [
    'transfer to', 'transferred to', 'sent to', 'debited', 'withdrawn', 'outgoing transfer',
    'تحويل إلى', 'تحويل الى', 'تم خصم', 'حوالة صادرة',
];
export const TRANSFER_IN_KEYWORDS = [
    'received', 'credited', 'deposited', 'transfer from', 'transferred from',
    'تم إيداع', 'إيداع', 'ايداع', 'استلام', 'تحويل من',
];
/**
 * The *other* side of a transfer, when the bank names an account rather than a
 * person.
 *
 * Sending to an IBAN or an account number is how rent, a contractor or family
 * abroad usually get paid, and those messages carry no name at all — so the
 * transfer had nothing to be recognised by, was never asked about, and never
 * counted. A transfer worth thousands is exactly the one that should not go
 * missing from "what can I spend".
 *
 * `your` is excluded on both sides, so a move between two accounts the user
 * owns still matches nothing and stays silent. Six digits minimum: a four-digit
 * group is the masked suffix of the user's own account, which appears in almost
 * every one of these messages.
 */
/*
 * Two shapes, and the distinction matters.
 *
 * Named — "A/C XXXX6789", "account ****4321" — the word or the mask says a bank
 * account follows, so four digits is enough. Masked beneficiary accounts are the
 * common case and were being dropped entirely: stripping the Xs left four
 * digits, and the rule below wanted six.
 *
 * Bare — "to 0123456789" — nothing says what the number is, so it has to be
 * long enough to be unmistakable. Four digits there would match the year in a
 * trailing date.
 */
const NAMED_ACCOUNT = String.raw `(?:a\/c|account|iban|beneficiary)\s*(?:no\.?)?\s*:?\s*(?:x{2,}|\*{2,})?\s*([A-Z]{2}\d{2}[A-Z0-9]{11,28}|\d{4,})`;
const MASKED_ACCOUNT = String.raw `(?:x{3,}|\*{3,})\s*(\d{4,})`;
const BARE_ACCOUNT = String.raw `([A-Z]{2}\d{2}[A-Z0-9]{11,28}|\d{6,})`;
export const TRANSFER_TO_ACCOUNT_PATTERNS = [
    new RegExp(String.raw `\bto\s+(?!your\b)\s*` + NAMED_ACCOUNT, 'i'),
    new RegExp(String.raw `\bto\s+(?!your\b)\s*` + MASKED_ACCOUNT, 'i'),
    new RegExp(String.raw `\bto\s+(?!your\b)\s*` + BARE_ACCOUNT, 'i'),
    // Arabic: "إلى الحساب 0123456789". حسابك — "your account" — is excluded.
    /(?:إلى|الى)\s*(?:الحساب|حساب)?(?!ك)\s*:?\s*(?:\*{2,}|x{2,})?\s*([A-Z]{2}\d{2}[A-Z0-9]{11,28}|\d{4,})/i,
];
export const TRANSFER_FROM_ACCOUNT_PATTERNS = [
    new RegExp(String.raw `\bfrom\s+(?!your\b)\s*` + NAMED_ACCOUNT, 'i'),
    new RegExp(String.raw `\bfrom\s+(?!your\b)\s*` + MASKED_ACCOUNT, 'i'),
    new RegExp(String.raw `\bfrom\s+(?!your\b)\s*` + BARE_ACCOUNT, 'i'),
    /من\s*(?:الحساب|حساب)(?!ك)\s*:?\s*(?:\*{2,}|x{2,})?\s*([A-Z]{2}\d{2}[A-Z0-9]{11,28}|\d{4,})/i,
];
export const TRANSFER_KEYWORDS = [
    'transfer', 'transferred', 'fund transfer', 'own account', 'between accounts',
    // "AED 2,000 moved to your Savings Account" parsed as nothing at all, so it
    // reached the review queue as an unreadable message rather than as the
    // internal move it plainly is.
    'moved to', 'outward remittance', 'inward remittance',
    'internal transfer', 'remittance', 'telegraphic transfer', 'iban',
    'تحويل', 'حوالة', 'تم التحويل',
    // Paying your own credit card bill moves money between two accounts you own.
    // The bank reports it twice — once from the account, once from the card — and
    // both read like a payment. Neither is spending, and counting either would
    // inflate the month by the size of the whole bill.
    'دفع إلى بطاقة الائتمان', 'استلام دفعة', 'credit card payment received',
    'payment to your credit card', 'payment received towards your card',
];
export const REFUND_KEYWORDS = [
    'refund', 'refunded', 'reversal', 'reversed', 'chargeback', 'returned',
    'استرداد', 'استرجاع', 'عكس القيد',
];
export const FEE_KEYWORDS = [
    'fee', 'fees', 'charges applied', 'service charge', 'annual fee', 'late payment fee',
    'interest charged', 'markup', 'foreign currency fee', 'processing fee', 'vat on',
    'رسوم', 'رسم', 'عمولة', 'فائدة',
];
export const ATM_KEYWORDS = [
    'atm', 'cash withdrawal', 'cash deposit', 'cash advance',
    'صراف', 'سحب نقدي', 'إيداع نقدي',
];
export const SALARY_KEYWORDS = ['salary', 'payroll', 'wps', 'راتب', 'رواتب'];
/**
 * Messages that look transactional but are not. Rejecting these early is what
 * keeps the ledger clean — an OTP or a marketing blast must never become a
 * transaction.
 */
export const NON_TRANSACTION_PATTERNS = [
    /\b(otp|one[-\s]?time\s*(password|pin)|verification\s*code|security\s*code)\b/i,
    /رمز\s*التحقق|كلمة\s*المرور\s*لمرة/,
    /\b(do not share|never share)\b.*\b(otp|pin|code)\b/i,
    /\b(offer|discount|promo|sale|win|congratulations|eligible for|pre[-\s]?approved|apply now)\b/i,
    /\bstatement\b[^.]{0,60}\b(is ready|is available|has been generated)\b/i,
    /\be-?statement\b/i,
    /\btotal (amount )?due\b/i,
    /\b(due date|minimum (amount )?due|payment is due|kindly pay)\b/i,
    /\b(balance enquiry|your available balance is)\b(?!.*\b(spent|purchase|debited|credited)\b)/i,
    /عرض\s*خاص|خصم\s*خاص|تهانينا/,
    /\b(password|login|sign[- ]?in) (attempt|changed|reset)\b/i,
    // Bank marketing and service acknowledgements. These carry long reference
    // numbers and words like "دفعات" that read as transactional, so they are
    // rejected explicitly rather than left to the absence of a currency token.
    /عروض\w*\s*حصرية|تطبق\s*الشروط\s*والأحكام/,
    /تم\s*استلام\s*الطلب|قيد\s*المعالجة|خلال\s*\d+\s*(?:يوم|أيام)\s*عمل/,
    /شهادة\s*المديونية|خطاب\s*مديونية/,
];
/**
 * Card/account number capture — deliberately never captures more than 4 digits.
 *
 * Every pattern here is anchored on a word meaning *ends with*. Emirates
 * Islamic also sends "البطاقة الائتمانية التي تبدأ بالرقم 442109" — that is a
 * six-digit BIN *prefix*, not a suffix, and storing its first four digits as
 * "the card" would silently attribute the transaction to a card that does not
 * exist.
 */
export const CARD_PATTERNS = [
    /card\s*(?:no\.?|number|ending|ending\s*(?:with|in))?\s*[:# ]?\s*(?:x{2,}|\*{2,}|\.{2,})?\s*(\d{4})\b/i,
    /(?:ending|ends)\s*(?:with|in)?\s*(\d{4})\b/i,
    /\b(?:x{4,}|\*{4,})(\d{4})\b/i,
    // "المنتهية بالرقم: 8264" — Emirates Islamic labels the suffix on its own line.
    /(?:المنتهية|المنتهي)\s*بالرقم\s*[:#]?\s*(\d{4})\b/,
    // "بطاقتك التي تنتهي أرقامها بـ 1871"
    /تنتهي\s*أرقامها\s*(?:بـ|ب)\s*[:#]?\s*(\d{4})\b/,
    // "بطاقتك المنتهية بـ 4521" — the noun carries possessive suffixes, so match a prefix.
    /بطاق\S*\s*(?:رقم|المنتهية|تنتهي)?\s*(?:بـ|ب)?\s*[:#]?\s*(?:\*+|x+)?\s*(\d{4})/i,
];
export const ACCOUNT_PATTERNS = [
    /account\s*(?:no\.?|number|ending|ending\s*(?:with|in))?\s*[:# ]?\s*(?:x{2,}|\*{2,})?\s*(\d{4})\b/i,
    /\ba\/c\s*(?:no\.?)?\s*[:# ]?\s*(?:x{2,}|\*{2,})?\s*(\d{4})\b/i,
    /حساب\S*\s*(?:رقم|المنتهي|ينتهي)?\s*(?:بـ|ب)?\s*[:#]?\s*(?:\*+|x+)?\s*(\d{4})/i,
];
export const REFERENCE_PATTERNS = [
    /\b(?:ref(?:erence)?|txn|transaction)\s*(?:no\.?|id|#)?\s*[:# ]\s*([A-Z0-9-]{4,32})\b/i,
    /\bauth(?:orization)?\s*(?:code|no\.?)?\s*[:# ]\s*([A-Z0-9-]{4,20})\b/i,
    /(?:رقم\s*(?:المرجع|العملية))\s*[:#]?\s*([A-Z0-9-]{4,32})/i,
];
export const BALANCE_PATTERNS = [
    // Credit cards report remaining credit rather than a balance. UAE banks say
    // this four different ways, and a card alert never says "الرصيد".
    /الائتمان\s*المتوفر\s*[:]?\s*(?:CUR)?\s*([\d.,]*\d)/i,
    /الحد\s*المتوفر\s*(?:هو)?\s*[:]?\s*(?:CUR)?\s*([\d.,]*\d)/i,
    /available\s*credit\s*[:]?\s*(?:CUR)?\s*([\d.,]*\d)/i,
    /(?:available|avl|remaining|current)?\s*(?:balance|bal)\s*(?:is|:)?\s*(?:CUR)\s*([\d.,]*\d)/i,
    /(?:balance|bal)\s*(?:is|:)?\s*([\d.,]*\d)\s*(?:CUR)/i,
    /الرصيد\s*(?:المتاح|الحالي|المتوفر)?\s*(?:هو)?\s*[:]?\s*(?:CUR)?\s*([\d.,]*\d)/i,
];
/**
 * Keyword matching that respects word boundaries for Latin-script keywords.
 *
 * A plain substring test makes "fee" match "COFFEE", which classified every
 * purchase at a coffee shop as a bank fee — in both languages, since merchant
 * names are Latin even in Arabic messages. Arabic keywords keep substring
 * semantics: Arabic is written without spaces around clitics, so `\b` would
 * miss legitimate matches like "بالبطاقة".
 */
const ASCII_ONLY = /^[\x20-\x7E]+$/;
const keywordMatchers = new Map();
export function containsKeyword(lowerText, keyword) {
    const k = keyword.toLowerCase();
    if (!ASCII_ONLY.test(k))
        return lowerText.includes(k);
    let re = keywordMatchers.get(k);
    if (!re) {
        const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Letter-only lookarounds, so "payment of" still matches mid-sentence but
        // "fee" does not match inside "coffee" or "feed".
        re = new RegExp(`(?<![a-z])${escaped}(?![a-z])`, 'i');
        keywordMatchers.set(k, re);
    }
    return re.test(lowerText);
}
/** Merchant capture, ordered most-specific first. */
export const MERCHANT_PATTERNS = [
    // Emirates Islamic sends a labelled block, one field per line:
    //   عملية دفع ببطاقة الائتمان / المنتهية بالرقم: 8264 / لدى: talabat pro, Dubai
    // The label is unambiguous, so it outranks every positional guess below.
    /لدى\s*[:]\s*([^\n]{2,60})/,
    // Arabic card-purchase template used by Emirates NBD and others:
    //   "تمت عملية شراء في AED 43.50 SWEET LAND SWEETS,ABU DHABI على البطاقة 1871"
    // The merchant sits between the amount and "على البطاقة". This must come
    // first: the generic Arabic "في" pattern below sits before the *amount* in
    // this template and would capture the amount itself as the merchant.
    new RegExp(`(?:${CURRENCY_ALT})\\s*[\\d,]+(?:\\.\\d{1,3})?\\s+([^\\n]{2,60}?)\\s*(?=على\\s*ال?بطاق)`),
    /\bat\s+([A-Z0-9][A-Za-z0-9&'.\-*\s]{1,48}?)(?=\s+(?:on|using|from|dated|at\s+\d)\b|[.,;]|$)/,
    /\b(?:to|towards)\s+([A-Z0-9][A-Za-z0-9&'.\-*\s]{1,48}?)(?=\s+(?:on|using|from|dated)\b|[.,;]|$)/,
    /\bmerchant\s*[:]\s*([^\n,;.]{2,48})/i,
    /\bat\s+([^\n,;]{2,48}?)(?=\s+on\b|[.,;]|$)/i,
    // "deducted ... for SALIK TOLL on 08/08/2026" — some banks name the payee after "for".
    /\bfor\s+([A-Z][A-Za-z0-9&'.\-*\s]{1,48}?)(?=\s+(?:on|using|dated|via)\b|[.,;]|$)/,
    /\bfrom\s+([A-Z0-9][A-Za-z0-9&'.\-*\s]{1,48}?)(?=\s+(?:on|dated)\b|[.,;]|$)/,
    /(?:لدى|في|من)\s+([^\n,;.]{2,48})/,
];
//# sourceMappingURL=patterns.js.map